import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Settings, Cookie } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { updatePostHogConsent } from "@/utils/posthog";

type CookiePreferences = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
};

const COOKIE_CONSENT_KEY = "seoro_cookie_consent";
const COOKIE_PREFERENCES_KEY = "seoro_cookie_preferences";

// Global function to open cookie settings
let openCookieSettings: (() => void) | null = null;

// Declare gtag function and Meta Pixel for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    enableMetaPixel?: () => void;
    twq?: (...args: any[]) => void;
    enableTwitterPixel?: () => void;
  }
}

export function openCookieSettingsDialog() {
  if (openCookieSettings) {
    openCookieSettings();
  }
}

export function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Expose function to open settings globally
    openCookieSettings = () => {
      setShowSettings(true);
    };

    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (!consent) {
      // Delay showing banner for better UX (1.5 seconds)
      const delayTimer = setTimeout(() => {
        setShowBanner(true);
        // Small additional delay for smooth animation
        setTimeout(() => {
          setIsVisible(true);
        }, 50);
      }, 1500);

      return () => {
        clearTimeout(delayTimer);
        openCookieSettings = null;
      };
    } else if (savedPreferences) {
      // Load saved preferences
      try {
        const parsed = JSON.parse(savedPreferences);
        setPreferences(parsed);
        applyCookiePreferences(parsed);
      } catch (e) {
        console.error("Error parsing cookie preferences:", e);
      }
    }

    return () => {
      openCookieSettings = null;
    };
  }, []);

  const applyCookiePreferences = (prefs: CookiePreferences) => {
    // Essential cookies are always enabled
    // Update Google Analytics consent based on user preferences
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied',
        'ad_storage': prefs.marketing ? 'granted' : 'denied'
      });
    }

    // Update PostHog consent based on analytics cookie preference
    // PostHog works in cookie-less mode but we still respect user preferences
    updatePostHogConsent();

    // Enable/disable Meta Pixel based on marketing cookie preference
    if (typeof window !== 'undefined') {
      if (prefs.marketing && typeof window.enableMetaPixel === 'function') {
        // Enable Meta Pixel if marketing cookies are accepted
        window.enableMetaPixel();
      } else if (!prefs.marketing && typeof window.fbq !== 'undefined') {
        // If marketing cookies are rejected, we can't fully disable the pixel
        // but we can stop tracking new events (Meta Pixel respects consent via consent mode)
        // Note: Meta Pixel doesn't have a built-in consent API like GA, so we rely on
        // not calling track events when consent is not given (handled in metaPixel.ts)
        // Silent - no console logging
      }
      
      // Twitter/X Pixel initialization
      // Note: Pixel is initialized immediately in index.html (always active)
      // This ensures it works even without explicit cookie consent
      // Events are always fired (matching Meta Pixel behavior)
      if (prefs.marketing && typeof window.twq !== 'undefined') {
        // Ensure pixel is initialized (harmless if already initialized)
        window.twq('config', 'qudrp');
      }
    }
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    savePreferences(allAccepted);
    setIsVisible(false);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setShowBanner(false);
    }, 300);
  };

  const handleRejectAll = () => {
    const onlyEssential: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    savePreferences(onlyEssential);
    setIsVisible(false);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setShowBanner(false);
    }, 300);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
    setShowSettings(false);
    setIsVisible(false);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setShowBanner(false);
    }, 300);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    applyCookiePreferences(prefs);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Cookie Banner */}
      <div 
        className={`fixed bottom-0 left-0 right-0 z-50 p-2 sm:p-4 lg:p-6 transition-all duration-300 ease-out ${
          isVisible 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-full pointer-events-none'
        }`}
      >
        <div className="max-w-7xl mx-auto bg-background border border-border rounded-xl sm:rounded-2xl shadow-2xl p-3 sm:p-5 lg:p-8 backdrop-blur-sm">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-2 sm:gap-3 lg:gap-6">
            {/* Icon and Content */}
            <div className="flex items-start gap-2 sm:gap-3 lg:gap-4 flex-1">
              <div className="flex-shrink-0 mt-0.5 sm:mt-1">
                <Cookie className="h-4 w-4 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base lg:text-xl font-bold text-foreground mb-1 sm:mb-2">
                  We Use Cookies
                </h3>
                <p className="text-xs sm:text-sm lg:text-base text-muted-foreground leading-snug sm:leading-relaxed">
                  <span className="hidden sm:inline">
                    We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                    By clicking "Accept All", you consent to our use of cookies. You can customize your preferences 
                    or reject non-essential cookies in the settings.
                  </span>
                  <span className="sm:hidden">
                    We use cookies to enhance your experience. Click "Accept All" to consent or customize your preferences.
                  </span>
                </p>
                <a
                  href="/privacy"
                  className="text-xs sm:text-sm text-primary hover:underline mt-1 sm:mt-2 inline-block"
                >
                  Learn more
                </a>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-row sm:flex-col lg:flex-row gap-2 sm:gap-2 lg:gap-3 w-full sm:w-full lg:w-auto lg:flex-shrink-0">
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                className="rounded-full px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 text-xs sm:text-sm font-semibold border-2 border-border/50 hover:bg-accent flex-1 sm:flex-none"
              >
                <Settings className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Customize</span>
                <span className="sm:hidden">Settings</span>
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="rounded-full px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 text-xs sm:text-sm font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 flex-1 sm:flex-none"
              >
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Cookie className="h-6 w-6 text-primary" />
              Cookie Preferences
            </DialogTitle>
            <DialogDescription className="text-base">
              Manage your cookie preferences. Essential cookies are required for the Service to function and cannot be disabled.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Essential Cookies */}
            <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-foreground">Essential Cookies</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Required for the Service to function properly. These cookies enable core functionality such as authentication, security, and session management.
                  </p>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <div className="w-12 h-6 bg-primary rounded-full flex items-center justify-end px-1 cursor-not-allowed opacity-60">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground italic">
                Always enabled - cannot be disabled
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground">Analytics Cookies</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Help us understand how visitors interact with our Service by collecting and reporting information anonymously. 
                    This helps us improve our platform and user experience.
                  </p>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <button
                    onClick={() =>
                      setPreferences({ ...preferences, analytics: !preferences.analytics })
                    }
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      preferences.analytics ? "bg-primary" : "bg-muted"
                    }`}
                  >
                    <div
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.analytics ? "translate-x-6" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground">Marketing Cookies</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Used to deliver personalized advertisements and track campaign performance. 
                    These cookies may be set by our advertising partners to build a profile of your interests.
                  </p>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <button
                    onClick={() =>
                      setPreferences({ ...preferences, marketing: !preferences.marketing })
                    }
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      preferences.marketing ? "bg-primary" : "bg-muted"
                    }`}
                  >
                    <div
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.marketing ? "translate-x-6" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="pt-4 border-t border-border/50">
              <p className="text-sm text-muted-foreground">
                You can change these preferences at any time by clicking the cookie icon in the footer or contacting us at{" "}
                <a href="mailto:support@seoro.so" className="text-primary hover:underline">
                  support@seoro.so
                </a>
                . For more information, please read our{" "}
                <a href="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </div>

          {/* Dialog Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border/50">
            <Button
              onClick={handleRejectAll}
              variant="outline"
              className="rounded-full flex-1 sm:flex-none border-2 border-border/50"
            >
              Reject All
            </Button>
            <Button
              onClick={() => setShowSettings(false)}
              variant="outline"
              className="rounded-full flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePreferences}
              className="rounded-full flex-1 sm:flex-none bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85"
            >
              Save Preferences
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

